import { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";

const ServerCaptchaWidget = forwardRef(function ServerCaptchaWidget({ onVerify }, ref) {
  const [token, setToken] = useState("");
  const [svg, setSvg] = useState("");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // Keep a stable ref to onVerify so fetchChallenge doesn't need it as a dep
  const onVerifyRef = useRef(onVerify);
  useEffect(() => { onVerifyRef.current = onVerify; });

  const fetchChallenge = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    setInput("");
    onVerifyRef.current(false);
    try {
      const res = await apiFetch(`/api/auth/captcha`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.token || !data.svg) throw new Error("Invalid response");
      setToken(data.token);
      setSvg(data.svg);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []); // stable — no deps needed

  useEffect(() => { fetchChallenge(); }, [fetchChallenge]);

  useImperativeHandle(ref, () => ({
    refresh: fetchChallenge,
    getChallenge: () => ({ token, answer: input }),
  }), [fetchChallenge, token, input]);

  const handleChange = (e) => {
    const val = e.target.value;
    setInput(val);
    // Button enables at 6 chars; actual correctness is verified server-side
    onVerify(val.length === 6);
  };

  return (
    <div className="captcha-widget">
      <label className="captcha-label">Security Check</label>
      <div className="captcha-image-row">
        {loading && (
          <div className="captcha-canvas captcha-placeholder">Loading…</div>
        )}
        {fetchError && (
          <div className="captcha-canvas captcha-placeholder captcha-placeholder-err">
            Failed —{" "}
            <button type="button" onClick={fetchChallenge} className="captcha-inline-retry">
              retry
            </button>
          </div>
        )}
        {!loading && !fetchError && (
          <img
            src={`data:image/svg+xml;base64,${btoa(svg)}`}
            alt="CAPTCHA challenge"
            className="captcha-canvas"
            draggable={false}
          />
        )}
        <button
          type="button"
          className="captcha-refresh"
          onClick={fetchChallenge}
          title="New code"
          disabled={loading}
        >
          ↺
        </button>
      </div>
      <input
        type="text"
        className="captcha-input"
        placeholder="Type the characters above"
        value={input}
        onChange={handleChange}
        maxLength={6}
        autoComplete="off"
        spellCheck={false}
        disabled={loading || fetchError}
      />
    </div>
  );
});

export default ServerCaptchaWidget;
